import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { apiGet, apiDelete, apiDownload, ApiError } from '../api/client';
import { formatMkd, formatDate } from '../lib/format';
import { btn } from '../components/FilterBar';
import StaffModal from '../components/StaffModal';
import StaffDetailsModal from '../components/StaffDetailsModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useToast } from '../components/Toast';

/**
 * Stafi. Ports frmStaf.
 *
 * The list can be ordered by whichever comes first — a birthday or a pay day —
 * which is what the desktop's sort dropdown did.
 */

interface StaffItem {
  id: number;
  emri: string;
  mbiemri: string;
  roli: string | null;
  biznesiID: number;
  biznesi: string;
  birthday: string | null;
  email: string | null;
  phone: string | null;
  startDate: string | null;
  monthlySalary: number;
  daysToNextSalary: number | null;
  daysToBirthday: number | null;
}

/** "Sot" / "Nesër" / "N ditë", as the desktop grid rendered it. */
function countdown(days: number | null): string {
  if (days === null) return '—';
  if (days === 0) return 'Sot';
  if (days === 1) return 'Nesër';
  return `${days} ditë`;
}

function countdownClass(days: number | null): string {
  if (days === null) return 'text-muted';
  if (days <= 1) return 'font-semibold text-emerald-700';
  if (days <= 7) return 'text-amber-700';
  return 'text-muted';
}

export default function Stafi() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'birthday' | 'salary' | 'name'>('birthday');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<{ id: number | null } | null>(null);
  const [viewing, setViewing] = useState<StaffItem | null>(null);
  const [deleting, setDeleting] = useState<StaffItem | null>(null);

  const listQuery = useQuery({
    queryKey: ['stafi', search, sort, includeInactive],
    queryFn: () =>
      apiGet<{ staff: StaffItem[]; activeCount: number }>('/stafi', {
        search,
        sort,
        includeInactive: includeInactive ? 'true' : undefined,
      }),
    placeholderData: (prev) => prev,
  });

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['stafi'] });
  }

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => apiDelete(`/stafi/${id}`),
    onSuccess: () => {
      toast.success('Punëtori u çaktivizua.');
      refresh();
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Veprimi dështoi.'),
  });

  const staff = listQuery.data?.staff ?? [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Stafi</h1>
          <p className="text-sm text-muted">
            {listQuery.data?.activeCount ?? 0} punëtorë aktivë
          </p>
        </div>
      </div>

      <div className="rounded-xl bg-white p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-52 flex-1">
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="staff-search">
              Kërko emër, mbiemër ose rol
            </label>
            <input
              id="staff-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Shkruani për të filtruar…"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-muted" htmlFor="staff-sort">
              Rendit sipas
            </label>
            <select
              id="staff-sort"
              value={sort}
              onChange={(e) => setSort(e.target.value as typeof sort)}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              <option value="birthday">Ditëlindjes më të afërt</option>
              <option value="salary">Rrogës më të afërt</option>
              <option value="name">Emrit</option>
            </select>
          </div>

          <label className="flex items-center gap-2 py-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Shfaq edhe joaktivët
          </label>

          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={() =>
                apiDownload('/stafi/eksport/lista', { search, sort }).catch((err) =>
                  toast.error(err instanceof ApiError ? err.message : 'Eksportimi dështoi.'),
                )
              }
              disabled={staff.length === 0}
              className={btn.secondary}
            >
              Eksporto Excel
            </button>
            <button type="button" onClick={() => setEditing({ id: null })} className={btn.primary}>
              Shto punëtor
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-muted">
                <th className="px-4 py-3 font-semibold">Emri</th>
                <th className="px-4 py-3 font-semibold">Roli</th>
                <th className="px-4 py-3 font-semibold">Biznesi</th>
                <th className="px-4 py-3 font-semibold">Ditëlindja</th>
                <th className="px-4 py-3 font-semibold">Kontakti</th>
                <th className="px-4 py-3 text-right font-semibold">Rroga mujore</th>
                <th className="px-4 py-3 font-semibold">Rroga vjen</th>
                <th className="px-4 py-3 text-right font-semibold">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {listQuery.isLoading && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted">
                    Duke ngarkuar…
                  </td>
                </tr>
              )}

              {!listQuery.isLoading && staff.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-muted">
                    Nuk ka punëtorë.
                  </td>
                </tr>
              )}

              {staff.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
                  <td className="px-4 py-2.5">
                    <button
                      type="button"
                      onClick={() => setViewing(s)}
                      className="font-medium text-brand-600 hover:underline"
                    >
                      {s.emri} {s.mbiemri}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-muted">{s.roli ?? '—'}</td>
                  <td className="px-4 py-2.5">{s.biznesi}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {formatDate(s.birthday)}
                    {s.daysToBirthday !== null && s.daysToBirthday <= 7 && (
                      <span className="ml-2 text-xs text-emerald-700">
                        🎂 {countdown(s.daysToBirthday)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5">
                    {s.email && (
                      <a href={`mailto:${s.email}`} className="block text-brand-600 hover:underline">
                        {s.email}
                      </a>
                    )}
                    {s.phone && <span className="block text-muted">{s.phone}</span>}
                    {!s.email && !s.phone && <span className="text-muted">—</span>}
                  </td>
                  <td className="tnum px-4 py-2.5 text-right">{formatMkd(s.monthlySalary)}</td>
                  <td className={`px-4 py-2.5 whitespace-nowrap ${countdownClass(s.daysToNextSalary)}`}>
                    {countdown(s.daysToNextSalary)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => setEditing({ id: s.id })}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100"
                      >
                        Ndrysho
                      </button>
                      <button
                        type="button"
                        onClick={() => setDeleting(s)}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                      >
                        Çaktivizo
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <StaffModal
          staffId={editing.id}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            toast.success('Të dhënat u ruajtën me sukses!');
            refresh();
          }}
        />
      )}

      {viewing && (
        <StaffDetailsModal
          staffId={viewing.id}
          fullName={`${viewing.emri} ${viewing.mbiemri}`}
          onClose={() => setViewing(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Konfirmo çaktivizimin"
          message={`${deleting.emri} ${deleting.mbiemri} do të hiqet nga lista aktive. Historiku i rrogave ruhet.`}
          confirmLabel="Çaktivizo"
          destructive
          busy={deactivateMutation.isPending}
          onCancel={() => setDeleting(null)}
          onConfirm={() => {
            deactivateMutation.mutate(deleting.id);
            setDeleting(null);
          }}
        />
      )}
    </div>
  );
}
